import { type Error as RawError } from '@scoring/types';
import type { ApiError } from './ApiError';

export interface ApiResult<TPayload> {
    data: TPayload;
    status: number;
    statusText: string | null;
    isSuccess: true;
    correlationId: string;
}

export interface ApiErrorResult {
    data: ApiError;
    status: number;
    statusText: string | null;
    isSuccess: false;
}

// export function isApiSuccess<TPayload>(result: ApiResult<TPayload> | unknown): result is ApiSuccessResult<TPayload> {
//     return (result as ApiResult<TPayload>).isSuccess === true;
// }

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function isApiErrorResult(value: unknown): value is ApiErrorResult {
    return (
        isObject(value) &&
        'status' in value &&
        typeof value.status === 'number' &&
        'statusText' in value &&
        (typeof value.statusText === 'string' || value.statusText === null) &&
        'isSuccess' in value &&
        value.isSuccess === false &&
        'data' in value
    );
}

export function ToFieldKeyOnly(input: string): string {
    const parts = input.split('.');
    return parts.length > 1 ? parts.slice(1).join('.') : input;
}

export function ToError(error: ApiErrorResult): RawError {
    const rawError: RawError = { message: error.data.message, validationErrors: [] };

    const general = error.data.validationErrors.filter((e) => e.field === 'generalErrors');
    const fieldErrors = error.data.validationErrors.filter((e) => e.field !== 'generalErrors');

    fieldErrors.forEach((ve) => {
        rawError.validationErrors.push({
            field: ToFieldKeyOnly(ve.field),
            errors: ve.errors,
            isGeneral: false,
        });
    });

    general.forEach((ve) => {
        rawError.validationErrors.push({
            field: 'general',
            errors: ve.errors,
            isGeneral: true,
        });
    });

    return rawError;
}

const defaultFallbackMessage = 'Something went wrong.';

export function getApiErrorMessage(error: unknown, fallback: string = defaultFallbackMessage): string {
    if (!isApiErrorResult(error)) {
        return fallback;
    }
    const { validationErrors, message } = error.data;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const allErrors = validationErrors.flatMap((ve) => ve.errors ?? []).filter(Boolean);
        if (allErrors.length > 0) {
            return allErrors.join('\n');
        }
    }
    if (typeof message === 'string' && message) {
        return message;
    }
    return fallback;
}
